import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ slug: "", name: "", price: 25, currency: "€", category: "", display_order: 0 });

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getProducts();
      setProducts(data.data);
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const data = await adminApi.getCategories();
      setCategories(data.data.filter(c => c.is_active));
    } catch (e) {
      console.error("Failed to load categories:", e);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const handleCreate = async () => {
    if (!form.slug || !form.name) {
      toast.error("Slug and name are required");
      return;
    }
    if (!form.category) {
      toast.error("Please select a category");
      return;
    }
    try {
      await adminApi.createProduct(form);
      toast.success("Product created");
      setShowCreate(false);
      setForm({ slug: "", name: "", price: 25, currency: "€", category: "", display_order: 0 });
      fetchProducts();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this product?")) return;
    try {
      await adminApi.deleteProduct(id);
      toast.success("Product deactivated");
      fetchProducts();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Products - Mockup Templates ({products.length})</h1>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button>Add Product</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Product Template</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Input placeholder="Slug (e.g. tshirt)" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} />
              <Input placeholder="Name (e.g. T-shirt)" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
              <div className="flex gap-2">
                <Input type="number" placeholder="Price" value={form.price} onChange={(e) => setForm({...form, price: parseFloat(e.target.value)})} />
                <Input placeholder="Currency" value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} className="w-20" />
              </div>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  {categories.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-gray-500">No categories - create one first</div>
                  ) : (
                    categories.map((cat) => (
                      <SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <Input type="number" placeholder="Display Order" value={form.display_order} onChange={(e) => setForm({...form, display_order: parseInt(e.target.value)})} />
              <Button onClick={handleCreate} className="w-full">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Order</TableHead>
              <TableHead className="w-16">Image</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Prompt</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id} className={!product.is_active ? "opacity-50" : ""}>
                <TableCell>{product.display_order}</TableCell>
                <TableCell>
                  {product.base_image_id ? (
                    <img
                      src={`${API}/api/images/${product.base_image_id}`}
                      alt={product.name}
                      className="w-10 h-10 object-cover rounded border"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center text-gray-400 text-xs">
                      No
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-mono text-sm">{product.slug}</TableCell>
                <TableCell className="font-medium">{product.name}</TableCell>
                <TableCell>{product.price}{product.currency}</TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {categories.find(c => c.slug === product.category)?.name || product.category}
                  </Badge>
                </TableCell>
                <TableCell>
                  {product.prompt_template ? (
                    <Badge variant="outline" className="text-green-600">Custom</Badge>
                  ) : (
                    <Badge variant="outline" className="text-gray-400">Default</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={product.is_active ? "default" : "destructive"}>
                    {product.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Link to={`/admin/products/${product.id}`}>
                      <Button variant="outline" size="sm">Edit</Button>
                    </Link>
                    {product.is_active && (
                      <Button variant="destructive" size="sm" onClick={() => handleDelete(product.id)}>
                        Deactivate
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
